import React, { useState, useEffect } from 'react';
import { 
  ChatBubbleLeftEllipsisIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { processFeedback } from '../../utils/feedbackFilter';
import { sendFeedbackToGitHub } from '../../utils/githubIssues';

const AutoFeedbackModal = () => {
  const [triggerData, setTriggerData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    autoRequest: true,
    frequency: 'normal', // low, normal, high
    skipExitPrompts: false
  });

  const { user, isAuthenticated, isBetaUser } = useAuth();

  // 설정 로드 (한 번만 실행)
  useEffect(() => {
    const savedSettings = JSON.parse(localStorage.getItem('feedbackSettings') || '{}');
    setSettings(prev => ({ ...prev, ...savedSettings }));
  }, []);

  // 이벤트 리스너 설정 (settings가 변경될 때마다 재설정)
  useEffect(() => {
    const handleAutoFeedbackRequest = (event) => {
      const data = event.detail;
      
      // 설정 확인
      if (!settings.autoRequest) return;
      if (settings.skipExitPrompts && data.isExitIntent) return;
      
      // 빈도 설정에 따른 필터링
      if (settings.frequency === 'low' && data.priority !== 'critical') {
        if (Math.random() > 0.3) return; // 30% 확률로만 표시
      } else if (settings.frequency === 'high') {
        // 모든 요청 허용
      } else { // normal
        if (data.priority === 'low' && Math.random() > 0.6) return; // 60% 확률
      }

      setTriggerData(data);
      setIsVisible(true);
    };

    window.addEventListener('autoFeedbackRequest', handleAutoFeedbackRequest);
    
    return () => {
      window.removeEventListener('autoFeedbackRequest', handleAutoFeedbackRequest);
    };
  }, [settings]);

  const getPriorityStyle = (priority) => {
    const styles = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[priority] || styles.medium;
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'high':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'medium':
        return <InformationCircleIcon className="w-4 h-4" />;
      case 'low':
        return <ClockIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const getContextualQuestions = (category, triggerName) => {
    const questions = {
      success: {
        correctStreak: [
          "연속 정답의 비결이 있나요?",
          "문제의 난이도는 적절했나요?",
          "해설이 도움이 되었나요?"
        ],
        examCompleted: [
          "전반적인 시험 경험은 어땠나요?",
          "시험 시간은 충분했나요?",
          "개선하고 싶은 기능이 있나요?"
        ],
        highScore: [
          "높은 점수 달성을 도운 기능이 있나요?",
          "학습 효과는 어땠나요?",
          "다른 사용자에게 추천하고 싶나요?"
        ]
      },
      difficulty: {
        wrongStreak: [
          "어떤 부분이 가장 어려웠나요?",
          "해설이 충분히 도움이 되었나요?",
          "추가 학습 자료가 필요하신가요?"
        ],
        repeatMistakes: [
          "이 유형의 문제에서 자주 실수하는 이유가 있나요?",
          "더 나은 해설이나 힌트가 필요한가요?",
          "학습 방법에 대한 조언이 필요하신가요?"
        ]
      },
      usage: {
        longSession: [
          "장시간 사용하시느라 수고많으셨어요!",
          "사용 중 불편한 점은 없었나요?",
          "휴식 알림 기능이 필요한가요?"
        ],
        frequentUser: [
          "자주 방문해주셔서 감사합니다!",
          "가장 유용한 기능은 무엇인가요?",
          "새로운 기능에 대한 제안이 있나요?"
        ]
      }
    };
    
    return questions[category]?.[triggerName] || ["자유롭게 의견을 남겨주세요!"];
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim()) return;

    setIsSubmitting(true);

    const feedbackData = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      text: feedbackText.trim(),
      trigger: triggerData,
      type: 'auto_triggered',
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    const userInfo = {
      userId: user?.id,
      isAuthenticated,
      isBetaUser,
      name: user?.name
    };

    try {
      const validation = processFeedback(feedbackData, userInfo);
      
      if (validation.success) {
        const existingFeedback = JSON.parse(localStorage.getItem('betaFeedback') || '[]');
        existingFeedback.push(validation.feedback);
        localStorage.setItem('betaFeedback', JSON.stringify(existingFeedback));
        
        // GitHub Issues로 자동 피드백 전송 시도 (Zero-Risk)
        try {
          const githubFeedback = {
            message: validation.feedback.text || triggerData.message,
            mood: 'neutral', // 자동 피드백은 중립으로 설정
            category: triggerData.category,
            priority: triggerData.priority,
            context: {
              ...triggerData.context,
              triggerType: 'auto_feedback',
              triggerName: triggerData.triggerName,
              autoTriggerMessage: triggerData.message,
              page: validation.feedback.page,
              viewport: validation.feedback.viewport,
              userId: validation.feedback.userId,
              isAuthenticated: validation.feedback.isAuthenticated,
              isBetaUser: validation.feedback.isBetaUser
            },
            timestamp: validation.feedback.timestamp,
            triggerName: triggerData.triggerName,
            isExitIntent: triggerData.isExitIntent || false,
            sessionId: `auto_${Date.now()}`
          };

          const githubResult = await sendFeedbackToGitHub(githubFeedback);
          
          if (githubResult && githubResult.success) {
            console.log(`✅ 자동 피드백이 GitHub Issue #${githubResult.issueNumber}로 생성되었습니다`);
            
            // GitHub Issue 정보를 localStorage에 추가
            const updatedFeedback = [...existingFeedback];
            const lastFeedback = updatedFeedback[updatedFeedback.length - 1];
            lastFeedback.githubIssue = githubResult;
            localStorage.setItem('betaFeedback', JSON.stringify(updatedFeedback));
          }
        } catch (githubError) {
          // GitHub 전송 실패는 사용자에게 알리지 않음 (Zero-Risk)
          console.log('GitHub 자동 피드백 전송 실패 (정상 동작):', githubError.message);
        }
        
        // 성공 처리
        setIsVisible(false);
        setFeedbackText('');
        setTriggerData(null);
        
        // 감사 메시지 표시
        window.dispatchEvent(new CustomEvent('showFeedbackThanks', {
          detail: { message: '자동 피드백에 응답해주셔서 감사합니다!' }
        }));
      }
    } catch (error) {
      console.error('Auto feedback submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setFeedbackText('');
    setTriggerData(null);
  };

  const handleDismissForever = () => {
    const newSettings = { ...settings, autoRequest: false };
    setSettings(newSettings);
    localStorage.setItem('feedbackSettings', JSON.stringify(newSettings));
    handleClose();
  };

  const handleSettingsUpdate = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    localStorage.setItem('feedbackSettings', JSON.stringify({ ...settings, ...newSettings }));
    setShowSettings(false);
  };

  if (!isVisible || !triggerData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* 설정 모달 */}
        {showSettings && (
          <div className="absolute inset-0 bg-white rounded-3xl z-10">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">자동 피드백 설정</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.autoRequest}
                      onChange={(e) => setSettings(prev => ({ ...prev, autoRequest: e.target.checked }))}
                      className="w-4 h-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <span className="text-sm font-medium text-gray-700">자동 피드백 요청 허용</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">요청 빈도</label>
                  <select
                    value={settings.frequency}
                    onChange={(e) => setSettings(prev => ({ ...prev, frequency: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-100"
                  >
                    <option value="low">낮음 (중요한 경우만)</option>
                    <option value="normal">보통 (균형있게)</option>
                    <option value="high">높음 (자주)</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.skipExitPrompts}
                      onChange={(e) => setSettings(prev => ({ ...prev, skipExitPrompts: e.target.checked }))}
                      className="w-4 h-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <span className="text-sm font-medium text-gray-700">페이지 이탈 시 피드백 요청 건너뛰기</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 py-2 px-4 text-gray-600 border border-gray-200 rounded-xl font-medium hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={() => handleSettingsUpdate(settings)}
                  className="flex-1 py-2 px-4 bg-pink-500 text-white rounded-xl font-medium hover:bg-pink-600"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-t-3xl">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <ChatBubbleLeftEllipsisIcon className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold">스마트 피드백</h3>
                <div className={`px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1 border ${getPriorityStyle(triggerData.priority)}`}>
                  {getPriorityIcon(triggerData.priority)}
                  <span>{triggerData.priority.toUpperCase()}</span>
                </div>
              </div>
              <p className="text-sm text-purple-100">AI가 적절한 타이밍을 감지했습니다</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/20 transition-colors"
              title="설정"
            >
              <CogIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/20 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 내용 */}
        <div className="p-6 space-y-6">
          {/* 트리거 메시지 */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">🤖</div>
              <div>
                <div className="font-medium text-purple-800 mb-2">{triggerData.message}</div>
                {triggerData.context && Object.keys(triggerData.context).length > 0 && (
                  <div className="text-sm text-purple-600">
                    {triggerData.context.streak && `연속 ${triggerData.context.streak}개`}
                    {triggerData.context.score && `점수: ${triggerData.context.score}점`}
                    {triggerData.context.duration && `사용시간: ${Math.round(triggerData.context.duration / 60000)}분`}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 상황별 가이드 질문 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              이런 점들에 대해 의견을 주세요:
            </label>
            <div className="bg-gray-50 rounded-lg p-3">
              {getContextualQuestions(triggerData.category, triggerData.triggerName).map((question, index) => (
                <div key={index} className="flex items-start space-x-2 mb-2 last:mb-0">
                  <span className="text-purple-500 text-sm mt-1">•</span>
                  <span className="text-sm text-gray-600">{question}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 피드백 입력 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              자세한 의견 (선택사항)
            </label>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="위의 질문들에 대한 의견이나 자유로운 피드백을 남겨주세요..."
              className="w-full h-24 p-3 border border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none resize-none text-sm"
              maxLength={300}
            />
            <div className="text-xs text-gray-400 mt-2">
              {feedbackText.length}/300 • 선택사항이지만 더 나은 서비스를 위해 의견을 주시면 감사하겠습니다
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="p-6 border-t border-gray-100 space-y-3">
          <div className="flex space-x-3">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-medium disabled:opacity-50 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>전송중...</span>
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-4 h-4" />
                  <span>피드백 전송</span>
                </>
              )}
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-3 text-gray-600 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              다음에
            </button>
          </div>
          
          <button
            onClick={handleDismissForever}
            className="w-full text-xs text-gray-400 hover:text-gray-600 py-2 transition-colors"
          >
            앞으로 자동 피드백 요청 받지 않기
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoFeedbackModal;